import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { Role } from '@pirouette/shared';
import { cn } from '@/lib/cn';
import { searchIntents, intentsForRole, type Intent, type IntentCategory } from '@/lib/intents';
import { getRecipe, recipeEntryRoute, type GuidedRecipe } from '@/lib/recipes';

// Always-visible "What do you want to do?" command bar.
//
// Deterministic, NO LLM. The user types a plain-language goal; we fuzzy-match
// it against the role-filtered intent catalog (label + synonyms) and show
// results with a breadcrumb and description. Selecting an intent navigates via
// the TanStack router (and, when the intent declares one, appends a trivial
// `openModal` query param the destination route can read). Intents tied to a
// guided recipe expand a short, read-only checklist instead of navigating away.
//
// Accessibility: this is an ARIA combobox with a listbox popup, full keyboard
// navigation (Up/Down/Enter/Escape/Home/End), `aria-activedescendant`, and a
// screen-reader live region announcing result counts. It is purely additive —
// it does not alter any existing AppShell behavior.

const RECENTS_STORAGE_KEY = 'pirouette:command-bar:recents:v1';
const MAX_RECENTS = 5;
const MAX_RESULTS = 8;

const CATEGORY_LABELS: Record<IntentCategory, string> = {
  navigate: 'Navigation',
  people: 'People',
  classes: 'Classes',
  billing: 'Billing',
  productions: 'Productions',
  competitions: 'Competitions',
  communication: 'Messaging',
  reports: 'Reports',
  settings: 'Settings',
  video: 'Video',
};

const ROLE_BREADCRUMB: Record<Role, string> = {
  admin: 'Admin',
  faculty: 'Faculty',
  parent: 'Parent',
  dancer: 'Dancer',
};

/**
 * Build the human breadcrumb shown under each result, e.g.
 * "Admin → Billing → Charge a family". Derived purely from role + category +
 * label so it never drifts from a hand-maintained string table.
 */
function breadcrumbFor(intent: Intent, role: Role | null | undefined): string {
  const head = role ? ROLE_BREADCRUMB[role] : 'Menu';
  const category = CATEGORY_LABELS[intent.category];
  return `${head} → ${category} → ${intent.label}`;
}

/** Read the persisted recent intent ids. Returns [] on any failure. NO PII. */
function readRecents(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENTS_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string').slice(0, MAX_RECENTS);
  } catch {
    return [];
  }
}

/** Persist `id` as the most-recent intent. Best-effort; swallows failures. */
function pushRecent(id: string): string[] {
  const next = [id, ...readRecents().filter((existing) => existing !== id)].slice(0, MAX_RECENTS);
  try {
    window.localStorage.setItem(RECENTS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Private mode / quota — recents are a convenience, not a requirement.
  }
  return next;
}

/**
 * A rendered row: either a matched intent or, when an intent carries a recipe,
 * the recipe it would expand. We keep the source intent so selection logic and
 * keys stay simple.
 */
interface CommandRow {
  intent: Intent;
  recipe?: GuidedRecipe;
}

export interface CommandBarProps {
  /** Viewer role — drives which intents/recipes are visible. */
  role: Role | null | undefined;
  /**
   * Navigation callback. Defaults to TanStack's `useNavigate`. Injectable so
   * the component is testable without mounting a full router. Receives the raw
   * route string and an optional modal query param.
   */
  onNavigate?: (route: string, openModal?: { key: string; value: string }) => void;
  /** Extra classes for the outer wrapper. */
  className?: string;
}

export function CommandBar({ role, onNavigate, className }: CommandBarProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [recentIds, setRecentIds] = useState<string[]>([]);
  const [expandedRecipe, setExpandedRecipe] = useState<GuidedRecipe | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();
  const labelId = useId();
  const optionIdPrefix = useId();

  // Hydrate recents on mount (client-only; localStorage is unavailable in SSR).
  useEffect(() => {
    setRecentIds(readRecents());
  }, []);

  const doNavigate = useCallback(
    (route: string, openModal?: { key: string; value: string }) => {
      if (onNavigate) {
        onNavigate(route, openModal);
        return;
      }
      // TanStack accepts a plain string `to`; append the modal param via
      // `search` when the destination route declares one.
      void navigate(
        openModal ? { to: route, search: { [openModal.key]: openModal.value } } : { to: route },
      );
    },
    [navigate, onNavigate],
  );

  // Result rows. With a query: fuzzy matches. Without: recents first (in their
  // saved order), then suggested intents in catalog order, de-duplicated.
  const rows = useMemo<CommandRow[]>(() => {
    const toRow = (intent: Intent): CommandRow => {
      const recipe = intent.recipeId ? getRecipe(intent.recipeId) : undefined;
      return recipe ? { intent, recipe } : { intent };
    };

    if (query.trim().length > 0) {
      return searchIntents(query, role, MAX_RESULTS).map(toRow);
    }

    const visible = intentsForRole(role);
    const byId = new Map(visible.map((intent) => [intent.id, intent]));
    const recentIntents = recentIds
      .map((id) => byId.get(id))
      .filter((intent): intent is Intent => intent != null);
    const recentIdSet = new Set(recentIntents.map((intent) => intent.id));
    const suggested = visible.filter((intent) => !recentIdSet.has(intent.id));
    return [...recentIntents, ...suggested].slice(0, MAX_RESULTS).map(toRow);
  }, [query, role, recentIds]);

  const showRecentsHeading = query.trim().length === 0 && recentIds.length > 0;

  // Keep the active option in range whenever the result set changes.
  useEffect(() => {
    setActiveIndex((current) => {
      if (rows.length === 0) return 0;
      return Math.min(current, rows.length - 1);
    });
  }, [rows]);

  const closeAndReset = useCallback(() => {
    setOpen(false);
    setActiveIndex(0);
  }, []);

  const selectRow = useCallback(
    (row: CommandRow) => {
      setRecentIds(pushRecent(row.intent.id));

      if (row.recipe) {
        // Recipes are read-only guidance: expand the checklist in place rather
        // than navigating. The user drives each step themselves. Close the
        // result listbox so the guide panel stands alone.
        setExpandedRecipe(row.recipe);
        closeAndReset();
        return;
      }

      doNavigate(row.intent.route, row.intent.openModal);
      setQuery('');
      closeAndReset();
    },
    [doNavigate, closeAndReset],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (!open) setOpen(true);
        setActiveIndex((i) => (rows.length === 0 ? 0 : (i + 1) % rows.length));
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (!open) setOpen(true);
        setActiveIndex((i) => (rows.length === 0 ? 0 : (i - 1 + rows.length) % rows.length));
        return;
      }
      if (event.key === 'Home') {
        if (open && rows.length > 0) {
          event.preventDefault();
          setActiveIndex(0);
        }
        return;
      }
      if (event.key === 'End') {
        if (open && rows.length > 0) {
          event.preventDefault();
          setActiveIndex(rows.length - 1);
        }
        return;
      }
      if (event.key === 'Enter') {
        if (open && rows.length > 0) {
          event.preventDefault();
          const row = rows[activeIndex];
          if (row) selectRow(row);
        }
        return;
      }
      if (event.key === 'Escape') {
        if (expandedRecipe) {
          setExpandedRecipe(null);
          return;
        }
        if (open) {
          event.preventDefault();
          closeAndReset();
        }
        return;
      }
    },
    [open, rows, activeIndex, selectRow, closeAndReset, expandedRecipe],
  );

  const activeOptionId = open && rows.length > 0 ? `${optionIdPrefix}-${activeIndex}` : undefined;

  return (
    <div className={cn('relative', className)}>
      <label id={labelId} htmlFor={`${listboxId}-input`} className="sr-only">
        What do you want to do?
      </label>

      {}
      <div
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-owns={listboxId}
        className="relative"
      >
        <input
          ref={inputRef}
          id={`${listboxId}-input`}
          type="text"
          role="searchbox"
          autoComplete="off"
          spellCheck={false}
          value={query}
          aria-labelledby={labelId}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeOptionId}
          placeholder="What do you want to do?"
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
            setExpandedRecipe(null);
            setActiveIndex(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            // Defer so a click on an option lands before we close.
            window.setTimeout(() => setOpen(false), 120);
          }}
          className={cn(
            'block w-full',
            'h-11 px-3.5',
            'rounded-[var(--radius-md)]',
            'border border-[var(--color-border)]',
            'bg-[var(--color-surface)]',
            'text-[var(--color-ink)] text-[var(--text-sm)]',
            'placeholder:text-[var(--color-ink-subtle)]',
            'transition-shadow duration-[var(--duration-fast)] ease-[var(--ease-out-quart)]',
            'hover:border-[var(--color-border-strong)]',
            'focus-visible:border-[var(--color-accent)] focus-visible:outline-none',
          )}
        />
      </div>

      {/* Screen-reader result announcement. */}
      <div aria-live="polite" className="sr-only">
        {open
          ? rows.length === 0
            ? 'No matching actions.'
            : `${rows.length} action${rows.length === 1 ? '' : 's'} available.`
          : ''}
      </div>

      {open && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Suggested actions"
          className={cn(
            'absolute z-[var(--z-dropdown,50)] mt-2 w-full',
            'max-h-[22rem] overflow-y-auto',
            'rounded-[var(--radius-md)]',
            'border border-[var(--color-border)]',
            'bg-[var(--color-surface-elevated)]',
            'shadow-[var(--shadow-md)]',
            'py-1',
          )}
        >
          {rows.length === 0 && (
            <li
              role="option"
              aria-selected={false}
              aria-disabled
              className="px-3.5 py-2 text-[var(--color-ink-muted)] text-[var(--text-sm)]"
            >
              No actions match “{query}”.
            </li>
          )}

          {showRecentsHeading && rows.length > 0 && (
            <li
              role="presentation"
              className="px-3.5 pt-2 pb-1 tracking-[var(--tracking-wide)] text-[var(--color-ink-subtle)] text-[var(--text-xs)] uppercase"
            >
              Recent &amp; suggested
            </li>
          )}

          {rows.map((row, index) => {
            const isActive = index === activeIndex;
            return (
              <li
                key={row.intent.id}
                id={`${optionIdPrefix}-${index}`}
                role="option"
                aria-selected={isActive}
                // Use onMouseDown so selection fires before the input's blur.
                onMouseDown={(event) => {
                  event.preventDefault();
                  selectRow(row);
                }}
                onMouseEnter={() => setActiveIndex(index)}
                className={cn(
                  'cursor-pointer px-3.5 py-2',
                  'border-l-2 border-transparent',
                  isActive && 'border-l-[var(--color-accent)] bg-[var(--color-surface-sunken)]',
                )}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium text-[var(--color-ink)] text-[var(--text-sm)]">
                    {row.intent.label}
                  </span>
                  {row.recipe && (
                    <span className="shrink-0 rounded-[var(--radius-sm)] bg-[var(--color-accent-muted)] px-1.5 py-0.5 text-[var(--color-ink)] text-[var(--text-xs)]">
                      Guide
                    </span>
                  )}
                </div>
                <span className="mt-0.5 block text-[var(--color-ink-subtle)] text-[var(--text-xs)]">
                  {breadcrumbFor(row.intent, role)}
                </span>
                <span className="mt-0.5 block text-[var(--color-ink-muted)] text-[var(--text-xs)]">
                  {row.intent.description}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {expandedRecipe && (
        <RecipePanel
          recipe={expandedRecipe}
          onClose={() => setExpandedRecipe(null)}
          onGoToStep={(route) => {
            doNavigate(route);
            setExpandedRecipe(null);
            setQuery('');
            closeAndReset();
          }}
        />
      )}
    </div>
  );
}

interface RecipePanelProps {
  recipe: GuidedRecipe;
  onClose: () => void;
  onGoToStep: (route: string) => void;
}

/**
 * Read-only guided checklist for a recipe. Renders the ordered steps with a
 * "Go" affordance per step that navigates to that step's route. No step
 * performs a destructive or state-changing action automatically.
 */
function RecipePanel({ recipe, onClose, onGoToStep }: RecipePanelProps) {
  const headingId = useId();
  const entryRoute = recipeEntryRoute(recipe);

  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        'absolute z-[var(--z-dropdown,50)] mt-2 w-full',
        'rounded-[var(--radius-md)]',
        'border border-[var(--color-border)]',
        'bg-[var(--color-surface-elevated)]',
        'shadow-[var(--shadow-md)]',
        'p-4',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2
            id={headingId}
            className="font-medium text-[var(--color-ink)] text-[var(--text-base)]"
          >
            {recipe.title}
          </h2>
          <p className="mt-0.5 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            {recipe.description}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded-[var(--radius-sm)] px-2 py-1 text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:bg-[var(--color-surface-sunken)]"
        >
          Close
        </button>
      </div>

      <ol className="mt-3 space-y-3">
        {recipe.steps.map((step, index) => (
          <li key={`${recipe.id}-step-${index}`} className="flex gap-3">
            <span
              aria-hidden
              className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--color-accent-muted)] text-[var(--color-ink)] text-[var(--text-xs)]"
            >
              {index + 1}
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-[var(--color-ink)] text-[var(--text-sm)]">
                {step.title}
              </p>
              <p className="mt-0.5 text-[var(--color-ink-muted)] text-[var(--text-xs)]">
                {step.detail}
              </p>
              {step.route && (
                <button
                  type="button"
                  onClick={() => onGoToStep(step.route!)}
                  className="mt-1 text-[var(--color-accent)] text-[var(--text-xs)] underline-offset-2 hover:underline"
                >
                  Go to this step
                </button>
              )}
            </div>
          </li>
        ))}
      </ol>

      {entryRoute && (
        <button
          type="button"
          onClick={() => onGoToStep(entryRoute)}
          className="mt-4 inline-flex h-9 items-center rounded-[var(--radius-md)] bg-[var(--color-accent)] px-3 font-medium text-[color:var(--color-ink-on-accent)] text-[var(--text-sm)] hover:bg-[var(--color-accent-hover)]"
        >
          Start
        </button>
      )}
    </section>
  );
}
